/**
 * Подпись времени матча в шапке карточки.
 *
 * Сервер отдаёт календарный старт (`ko`) и линию. Подпись считается здесь, из
 * этих двух и часов стора: серверное «через N минут» протухает между кадрами,
 * а возраст вилки уже тикает тем же `now`. Конца матча нет: в CS с овертаймами
 * это враньё, и экран его не обещает.
 *
 * Нет ни линии, ни старта — пустая строка: закон экрана, он молчит.
 */

const MIN = 60;
const HOUR = 3600;
const DAY = 86400;

export interface WhenFork {
  line?: string | null;
  ko?: string | null;
}

export interface WhenOpts {
  /** Для тестов и предсказуемой «в вс, 21:00». Боевой вызов не передаёт. */
  locale?: string;
  timeZone?: string;
}

function kickoffMs(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const text = raw.trim();
  if (text === '') return null;
  const ms = Date.parse(text);
  return Number.isFinite(ms) ? ms : null;
}

function atLeast(units: number): number {
  return Math.max(1, Math.floor(units));
}

function ago(seconds: number): string {
  if (seconds >= DAY) return `старт был ${atLeast(seconds / DAY)} дн назад`;
  if (seconds >= HOUR) return `старт был ${atLeast(seconds / HOUR)} ч назад`;
  return `старт был ${atLeast(seconds / MIN)} мин назад`;
}

function until(seconds: number, start: number, opts?: WhenOpts): string {
  if (seconds >= DAY) return clock(start, opts);
  if (seconds >= HOUR) return `до начала ${atLeast(seconds / HOUR)} ч`;
  return `до начала ${atLeast(seconds / MIN)} мин`;
}

function elapsed(seconds: number): string {
  if (seconds < HOUR) return `идёт ${atLeast(seconds / MIN)} мин`;
  const hours = Math.floor(seconds / HOUR);
  const minutes = Math.floor((seconds % HOUR) / MIN);
  return minutes === 0 ? `идёт ${hours} ч` : `идёт ${hours} ч ${minutes} мин`;
}

function clock(ms: number, opts?: WhenOpts): string {
  const parts = new Intl.DateTimeFormat(opts?.locale, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: opts?.timeZone,
  }).formatToParts(new Date(ms));
  const weekday = (parts.find((part) => part.type === 'weekday')?.value ?? '')
    .replace(/\.$/, '')
    .toLowerCase();
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  return `в ${weekday}, ${hour}:${minute}`;
}

/**
 * Текст блока шапки или `null`, если говорить не о чем.
 *
 * `now` — часы стора в миллисекундах, те же, что возраст вилки.
 */
export function whenText(fork: WhenFork, now: number, opts?: WhenOpts): string | null {
  const live = fork.line === 'live';
  const start = kickoffMs(fork.ko);
  if (start === null) return live ? 'лайв' : null;
  const seconds = Math.round((start - now) / 1000);
  if (live) {
    if (seconds > 0) return 'лайв';
    return `лайв · ${elapsed(-seconds)}`;
  }
  if (seconds > 0) return until(seconds, start, opts);
  return ago(-seconds);
}
